import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../../reducers';
import { Api } from '../../../../typings/api';
import {
	Wrapper,
	Header,
	ButtonConfirm,
	DriveListWrapper,
	EmptyFolderLabel,
	TitleBlack,
	ButtonClose,
	HeaderWithBorder,
} from '../styles';
import Portal from '../../../Portal';
import HeaderLogo from '../../../../containers/MenuManager/Header/Logo';
import Toggle from '../../../Toggle';
import Breadcrumbs from '../Breadcrumbs';
import DriveItemList from '../../../DriveItemList';
import { ListDisplayType } from '../../../../containers/DriveItemList/constants';
import {
	getOrFetchDriveItemMeta,
	setBreadcrumbs,
	resetBreadcrumbs,
	fetchDriveItem
} from '../../../../containers/DriveItemList/actions';
import { DriveListState } from '../../../../containers/DriveItemList/reducer';
import Utils from '../../../../utils';
import UserAgentUtils from '../../../../utils/userAgent';
import useResetDrivePage from '../../../../utils/customHooks/useResetDrivePage';
import { getDriveItemIds, isFetchingState } from '../utils';
import SpreadsSelector from '../../../../containers/Editor/CombinePanel/SpreadsSelector';
import { NewStyleColor } from '../../../../utils/styles-constants';

interface FromAppProps {
	driveList: DriveListState;
	currentDriveId: number | undefined;
	privateDirId: number;
	sharedDirId: number;
	sourcesDirId: number;
	applicationId: number;
	isValidResource: (item: Api.DriveItem) => boolean;
	onClose: () => void;
	onSelect: (items: Api.DriveItem[]) => void;
	dispatch: Dispatch<StoreState>;
	actionButtonColor?: string;
	isMultipleSelection?: boolean;
	className?: string;
}

enum RootDriveLabels {
	my_documents = 'messages.my_documents',
	my_app = 'messages.my_app',
	my_drive = 'messages.my_drive',
}

interface RootDriveState {
	id: number;
	label: RootDriveLabels;
}

const emptyFolderResources = { resources: [], extraDriveIds: [] };

const getFolderResources = (
	currentDriveId: number | undefined,
	{ driveItems }: DriveListState,
	privateDirId: number,
	sharedDirId: number,
	sourcesDirId: number,
): { resources: Api.DriveItemResource[], extraDriveIds: number[] } => {
	if (!currentDriveId) {
		return emptyFolderResources;
	}

	let resources: Api.DriveItemResource[];
	const driveFolder = driveItems[currentDriveId];

	if (!driveFolder || !driveFolder.resources) {
		return emptyFolderResources;
	}

	resources = driveFolder.resources;
	const isPrivateDir = currentDriveId === privateDirId;
	const extraDriveIds = isPrivateDir ? [sharedDirId, sourcesDirId] : [];

	return { resources, extraDriveIds };
};

const FromApp = ({
	driveList,
	currentDriveId,
	privateDirId,
	sharedDirId,
	sourcesDirId,
	applicationId,
	isValidResource,
	onClose,
	onSelect,
	dispatch,
	actionButtonColor,
	isMultipleSelection,
	className,
}: FromAppProps) => {
	const rootDriveConfig: { [k: string]: RootDriveState } = {
		[applicationId]: {
			id: applicationId,
			label: Utils.isEmbedMode() ? RootDriveLabels.my_drive : RootDriveLabels.my_app,
		},
		[privateDirId]: {
			id: privateDirId,
			label: RootDriveLabels.my_documents,
		},
	};
	const [rootDriveId, setRootDriveId] = React.useState(applicationId);
	const [selectedItemsIds, setSelectedItemsIds] = React.useState<number[]>([]);
	const [selectedItemId, setSelectedItemId] = React.useState<number>();
	const [isSpreadsSelectorDisplayed, setIsSpreadsSelectorDisplayed] = React.useState(false);
	const shouldResetDrivePage = useResetDrivePage([currentDriveId]);

	const getFolder = (id: number, label: string): void => {
		const isSharedDir = id === sharedDirId;

		if (id === sharedDirId) {
			label = 'messages.shared_with_me';
		}

		if (id === sourcesDirId) {
			label = 'messages.sources';
		}

		dispatch(setBreadcrumbs({ id, label }));
		setSelectedItemsIds([]);

		if (id && (!UserAgentUtils.isMobileApp || isSharedDir)) {
			dispatch(getOrFetchDriveItemMeta(id, true, true, isSharedDir));
		}
	};

	const handleRootDriveToggleClick = (id: number): void => {
		setRootDriveId(id);
		dispatch(resetBreadcrumbs());
		getFolder(id, rootDriveConfig[id].label);
	};

	const handleBreadcrumbClick = (id: number, label: string): void => {
		getFolder(id, label);
	};

	const handleDriveItemClick = (item: Api.DriveItem): void => {
		const { type, id, label } = item;

		if (type === Api.DriveItemType.Directory) {
			return getFolder(id, label);
		}

		if (isMultipleSelection) {
			setSelectedItemsIds(prevSelectedItemsIds => (
				prevSelectedItemsIds.includes(id)
					? prevSelectedItemsIds.filter(itemId => itemId !== id)
					: [...prevSelectedItemsIds, id]
			));
		} else {
			if (Utils.isEmptyPlaylistMode()) {
				setSelectedItemId(item.id);
				setIsSpreadsSelectorDisplayed(true);
			} else if (onSelect) {
				onSelect([item]);
			}
			onClose();
		}
	};

	const handleConfirm = () => {
		const selectedItems = selectedItemsIds.map(id => driveList.driveItems[id]);
		if (onSelect) {
			onSelect(selectedItems);
		}
		onClose();
	};

	React.useEffect(() => {
		getFolder(rootDriveId, rootDriveConfig[rootDriveId].label);
		return () => {
			dispatch(resetBreadcrumbs());
		};
	}, []);

	const { driveItems, driveItemsFetchStatus, breadcrumbs } = driveList;
	const isFetching = isFetchingState(currentDriveId, driveItemsFetchStatus);
	const { resources, extraDriveIds } = getFolderResources(currentDriveId, driveList, privateDirId, sharedDirId, sourcesDirId);
	const driveItemIds = getDriveItemIds(resources, driveList, isValidResource, extraDriveIds, ({ id }) => id !== privateDirId);
	const isEmptyFolder = !isFetching && driveItemIds.length < 1;

	React.useEffect(() => {
		if (Utils.isPlaylistMode()) {
			resources
				.forEach(resource => {
					if (!driveItems[resource.id]) {
						dispatch(fetchDriveItem(resource.id, currentDriveId)).then();
					}
				});
		}
	}, [resources]);

	const selectItemLabel = Utils.isPlaylistMode() ? 'messages.select_playlist_item' : 'messages.select_publication_item';

	return (
		<>
			{!isSpreadsSelectorDisplayed &&
				<Portal parentNode={document.body}>
					<Wrapper className={className}>
						<Header>
						<HeaderLogo isDisabled={true} />
							{isMultipleSelection &&
								<ButtonConfirm
									type="button"
									onClick={handleConfirm}
									color={actionButtonColor}
									disabled={selectedItemsIds.length === 0}
								>
									<Translate value="messages.ok" />
								</ButtonConfirm>
							}
						</Header>
						{!Utils.isEmbedMode() &&
							<Toggle
								items={Object.values(rootDriveConfig)}
								activeId={rootDriveId}
								onItemClick={handleRootDriveToggleClick}
							/>
						}
						<HeaderWithBorder>
						<ButtonClose
							iconWidth="10px"
							iconHeight="10px"
							onClick={onClose}
							color={NewStyleColor.Black}
							disabled={Utils.isEmptyPlaylistMode()}
						>
							{Utils.getSvgIcon('iconset_close-2')}
						</ButtonClose>
						<TitleBlack value={selectItemLabel} />
						</HeaderWithBorder>
						<Breadcrumbs
							items={breadcrumbs}
							onItemClick={handleBreadcrumbClick}
						/>
						<DriveListWrapper>
							{isEmptyFolder && <EmptyFolderLabel value="messages.no_assets" />}
							<DriveItemList
								hasBottomBar={false}
								searchConfig={undefined}
								displayType={ListDisplayType.Row}
								storeDriveItems={driveItems}
								driveItemIdsToDisplay={driveItemIds}
								folderId={currentDriveId}
								sharedDirId={undefined}
								sourcesDirId={undefined}
								isPrivateDir={false}
								isFetching={isFetching}
								showUnreadBadge={false}
								hasActions={false}
								hasSelectionIcon={false}
								navigationItems={[]}
								shouldGetCustomTemplates={false}
								isEditMode={false}
								isEditButtonDisplayed={false}
								isDisplayTypeDropdownDisplayed={false}
								isLoading={false}
								isTheaterOpened={false}
								shouldResetPage={shouldResetDrivePage}
								onDriveItemClick={handleDriveItemClick}
								isMultipleSelection={isMultipleSelection}
								selectedItemsIds={selectedItemsIds}
								dispatch={dispatch}
							/>
						</DriveListWrapper>
					</Wrapper>
				</Portal>
			}
			{isSpreadsSelectorDisplayed && Utils.isPlaylistMode() && selectedItemId &&
				<SpreadsSelector
					resourceId={selectedItemId}
					onClose={() => setIsSpreadsSelectorDisplayed(false)}
				/>
			}
		</>
	);
};

export default FromApp;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ResourceSelector/DrivePicker/FromApp/index.tsx